import type { MonitorAndProtectModelAdsApiModelAdTableRow } from "@similarweb/pro-api-client";
import type { ITableProps } from "@similarweb/pro-logic-components";

import type {
    IActionCell,
    IAdCell,
    IAdTypeCell,
    IDomainCell,
    ITextCell,
} from "app/pages/digital-marketing/monitor-and-protect/shared/ui/table/cells";
import type { Translate } from "pro-features/components/WithTranslation";
import { getFaviconUrl } from "app/services/sitesService/sitesUtils";

import { NumberFormatter } from "../../services/number-formatter";
import type { TableRow } from "../../shared/ui/table/builders";
import { ColumnBuilder, ConfigBuilder, RowBuilder } from "../../shared/ui/table/builders";

export type IExampleTableRow = TableRow<{
    title: IAdCell;
    adType: IAdTypeCell;
    domain: IDomainCell;
    keywordsCount: IActionCell<number>;
    clicksCount: ITextCell<number>;
    avgRank: ITextCell<number>;
}>;

const createParentColumnBuilder = (
    translateFn: Translate,
    pagination?: ITableProps["pagination"],
    rowCount?: number,
) => {
    const builder = new ColumnBuilder<IExampleTableRow>()
        .addRowNumberColumn("rowNum", {
            title: "",
            width: 60,
            currentPage: pagination?.current,
            pageSize: pagination?.pageSize,
        })
        .addColumn("title", {
            title: `${translateFn("monitor_and_protect.page.ads.table.columns.ad.title_simple")} (${NumberFormatter.toAbbr(rowCount ?? 0)})`,
            sorter: true,
            tooltipTitle: translateFn("monitor_and_protect.page.ads.table.columns.ad.tooltip.title"),
            tooltipSubtitle: translateFn("monitor_and_protect.page.ads.table.columns.ad.tooltip.description"),
        })
        .addColumn("adType", {
            title: translateFn("monitor_and_protect.page.ads.table.columns.adType.title"),
            width: 90,
            sorter: true,
            tooltipTitle: translateFn("monitor_and_protect.page.ads.table.columns.adType.tooltip.title"),
            tooltipSubtitle: translateFn("monitor_and_protect.page.ads.table.columns.adType.tooltip.description"),
        })
        .addColumn("domain", {
            title: translateFn("monitor_and_protect.page.ads.table.columns.domain.title"),
            width: 150,
            sorter: true,
            tooltipTitle: translateFn("monitor_and_protect.page.ads.table.columns.domain.tooltip.title"),
            tooltipSubtitle: translateFn("monitor_and_protect.page.ads.table.columns.domain.tooltip.description"),
        })
        .addColumn("keywordsCount", {
            title: translateFn("monitor_and_protect.page.ads.table.columns.keywords.title"),
            width: 100,
            sorter: true,
            tooltipTitle: translateFn("monitor_and_protect.page.ads.table.columns.keywords.tooltip.title"),
            tooltipSubtitle: translateFn("monitor_and_protect.page.ads.table.columns.keywords.tooltip.description"),
        });

    builder.addColumn("avgRank", {
        title: translateFn("monitor_and_protect.page.ads.table.columns.lastPosition.title"),
        width: 120,
        sorter: true,
        align: "right",
        tooltipTitle: translateFn("monitor_and_protect.page.ads.table.columns.lastPosition.tooltip.title"),
        tooltipSubtitle: translateFn("monitor_and_protect.page.ads.table.columns.lastPosition.tooltip.description"),
    });

    return builder;
};

export const getParentRowBuilder = (data: {
    onExpand: (itemIndex: MonitorAndProtectModelAdsApiModelAdTableRow) => void;
    translateFn: Translate;
    advertiserHoverActions: IDomainCell["hoverActions"];
}) => {
    const builder = new RowBuilder<MonitorAndProtectModelAdsApiModelAdTableRow, IExampleTableRow>(
        (e) => e.adId,
        createParentColumnBuilder(data.translateFn),
    )
        .mapField("title", (e) => ({
            type: "ad",
            title: e.title,
            iconUrl: getFaviconUrl(e.advertiser),
            description: e.description,
            domain: e.advertiser,
            data: e.urlShown,
            isPla: e.adType === "pla",
            imageUrl: e.image,
            price: e.price,
            adUrl: e.url,
        }))
        .mapField("adType", (e) => ({
            type: "adType",
            title: e.adType,
            adType: e.adType,
            data: e.adType,
        }))
        .mapField("domain", (e) => ({
            type: "domain",
            data: e.advertiser,
            domainUrl: e.advertiser,
            iconUrl: getFaviconUrl(e.advertiser),
            title: e.advertiser,
            hoverActions: data.advertiserHoverActions,
        }))
        .mapField("keywordsCount", (e) => ({
            type: "action",
            text: e.keywordsCount,
            data: e.keywordsCount,
            onClick: () => data.onExpand?.(e),
        }));

    builder.mapField("avgRank", (e) => ({
        type: "text",
        text: e.avgRank.toFixed(1),
        data: e.avgRank,
    }));

    return builder;
};

export const createConfigBuilder = (
    translateFn: Translate,
    pagination?: ITableProps["pagination"],
    rowCount?: number,
) => {
    return new ConfigBuilder(createParentColumnBuilder(translateFn, pagination, rowCount));
};
